import store from "@/src/store/index.js";
import DashboardService from "@/src/api/DashboardService.js";
import {
  cardComponentKeyMapping,
  cardOptionsMapping,
  ownerFieldByObject,
  defaultOwnerField,
  dateFieldByObject,
  defaultDateField,
} from "@/src/constants/dashboardConstants.js";
import {
  filterOperators,
  operatorsWithoutValue,
} from "@/src/constants/analyticsFilterConstants.js";
import { booleanOptions } from "@/src/constants/crmConstants.js";

export const toggleCardList = async () => {
  try {
    await store.dispatch("dashboard/toggleCardListState");
  } catch (error) {
    console.error("An error occurred while toggling card list: ", error);
  }
};

export const toFetchComponents = async () => {
  try {
    const response = await DashboardService.builder.fetchComponents();
    console.log("[toFetchComponents] API response:", response);

    if (response.success) {
      const allComponents = response.data.components;
      const filteredComponents = allComponents.filter((component) => {
        const hasMapping = cardComponentKeyMapping.hasOwnProperty(
          component.component_key,
        );
        if (!hasMapping) {
          console.warn(
            `[toFetchComponents] Filtering out component: ${component.name} (key: ${component.component_key}) - no mapping found`,
          );
        }
        return hasMapping;
      });

      const fetchedComponents = filteredComponents.map((component) => ({
        ...component,
        icon: cardOptionsMapping[component.component_type]?.icon || null,
      }));

      console.log(
        "[toFetchComponents] Final components after filtering:",
        fetchedComponents,
      );
      await store.dispatch("dashboard/setComponents", fetchedComponents);
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching components for the card list: ",
      error,
    );
  }
};

const resolveOptionLabel = (value, options = [], dataType = "") => {
  if (value === null || value === undefined || value === "") return "";
  if (value === -1 || value === "-1") return "Only Me";

  if (dataType === "boolean") {
    const boolVal = typeof value === "string" ? value === "true" : value;
    const opt = booleanOptions.find((o) => o.value === boolVal);
    return opt?.label || String(value);
  }

  if (value && typeof value === "object") {
    return value.name || value.label || String(value.id || value);
  }

  if (options.length > 0) {
    const match = options.find(
      (opt) => opt.id === value || opt.value === value || opt.slug === value,
    );
    if (match) return match.name || match.label || String(value);
  }

  if (
    typeof value === "string" &&
    (value.includes("-") || value.includes("_"))
  ) {
    return value.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return String(value);
};

export const formatFilterDisplayLine = (
  filterKey,
  filterValue,
  targetObject = null,
  allObjects = [],
) => {
  if (!filterKey || !filterValue?.operator) return null;

  let propName = filterKey;
  let prop = null;

  const findProp = (obj) =>
    obj?.properties?.find((p) => p.slug === filterKey || p.id === filterKey);

  prop = findProp(targetObject);
  if (!prop) {
    for (const obj of allObjects) {
      prop = findProp(obj);
      if (prop) break;
    }
  }
  if (prop) propName = prop.name || prop.label || prop.slug || filterKey;

  const op = filterOperators.find((o) => o.id === filterValue.operator);
  const opLabel = op?.label || filterValue.operator.replace(/_/g, " ");

  if (operatorsWithoutValue.includes(filterValue.operator)) {
    return `${propName} ${opLabel}`;
  }

  const options = prop?.options || [];
  const dataType = prop?.type || prop?.dataType || "";
  const rawValue = filterValue.value;

  let displayValue;
  if (Array.isArray(rawValue)) {
    displayValue = rawValue
      .map((v) => resolveOptionLabel(v, options, dataType))
      .join(", ");
  } else {
    displayValue = resolveOptionLabel(rawValue, options, dataType);
  }

  return `${propName}: ${opLabel} ${displayValue}`.trim();
};

export const resolvePropertyName = (obj, slug) => {
  if (!obj?.properties) return slug;
  const prop = obj.properties.find(
    (p) => p.slug === slug || p.id === slug || p.key === slug,
  );
  return prop?.name || prop?.label || slug;
};

export const getOwnerFieldSlug = (objectSlug) => {
  return ownerFieldByObject[objectSlug] || defaultOwnerField;
};

export const getDateFieldSlug = (objectSlug) => {
  return dateFieldByObject[objectSlug] || defaultDateField;
};

export const getOwnerFieldDisplayName = (obj) => {
  const ownerSlug = getOwnerFieldSlug(obj?.slug);
  const name = resolvePropertyName(obj, ownerSlug);
  if (name === ownerSlug) {
    return ownerSlug
      .replace(/_id$/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return name;
};
